import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Activity,
  Loader2,
  RefreshCw,
  Send,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Smartphone,
  Clock,
  History,
  FileText,
  Image as ImageIcon,
  Video,
  MessageSquare,
  Shield,
  Zap,
} from "lucide-react";

const formatPhone = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 5)} ${digits.slice(5, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return raw;
};

const variantStyles = {
  ok: {
    border: "border-emerald-200",
    bg: "bg-emerald-50/30",
    icon: "text-emerald-600",
    badge: "border-emerald-200 text-emerald-700",
  },
  down: {
    border: "border-red-200",
    bg: "bg-red-50/30",
    icon: "text-red-600",
    badge: "border-red-200 text-red-700",
  },
  warn: {
    border: "border-amber-200",
    bg: "bg-amber-50/30",
    icon: "text-amber-600",
    badge: "border-amber-200 text-amber-700",
  },
};

// One lane card (OTP or BULK). Compact, responsive, with its own Test button.
const LaneCard = ({ lane, data, onTest, onRefresh, loading }) => {
  const isHealthy = data?.connected && data?.smartphone_connected;
  const fallback = data?.fallback;
  const variant = !data?.configured
    ? "warn"
    : isHealthy
      ? (fallback ? "warn" : "ok")
      : "down";
  const vs = variantStyles[variant];
  const statusText = !data?.configured
    ? "Não configurado"
    : isHealthy
      ? (fallback ? "Fallback ativo" : "Conectado")
      : "Desconectado";
  const StatusIcon = !data?.configured
    ? AlertTriangle
    : isHealthy
      ? CheckCircle2
      : XCircle;
  const LaneIcon = lane === "otp" ? Shield : Zap;

  return (
    <Card
      className={`${vs.border} ${vs.bg}`}
      data-testid={`zapi-health-card-${lane}`}
    >
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <LaneIcon className={`w-5 h-5 ${vs.icon} flex-shrink-0`} />
            <div className="min-w-0">
              <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">
                {lane === "otp" ? "Lane OTP" : "Lane Bulk"}
              </h4>
              <p className="text-[10px] text-slate-500">
                {lane === "otp"
                  ? "Verificação · baixo volume"
                  : "Broadcasts + PDFs · alto volume"}
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <StatusIcon className={`w-3.5 h-3.5 ${vs.icon}`} />
                <Badge
                  variant="outline"
                  className={`text-xs ${vs.badge}`}
                  data-testid={`zapi-status-badge-${lane}`}
                >
                  {statusText}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              disabled={loading}
              title="Verificar"
              className="h-7 px-2"
              data-testid={`zapi-refresh-${lane}`}
            >
              {loading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
            </Button>
            <Button
              size="sm"
              onClick={() => onTest(lane)}
              disabled={!data?.configured}
              className="h-7 px-2 bg-sky-600 hover:bg-sky-700 text-white"
              data-testid={`zapi-test-${lane}`}
            >
              <Send className="w-3 h-3 mr-1" />
              <span className="text-xs">Testar</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div>
            <p className="text-slate-500">Número</p>
            <p
              className="font-medium font-mono text-slate-900 truncate"
              data-testid={`zapi-phone-${lane}`}
            >
              {data?.phone ? formatPhone(data.phone) : "—"}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Instância</p>
            <p className="font-medium font-mono text-slate-700 text-[10px] truncate">
              {data?.instance_id_masked || "—"}
            </p>
          </div>
        </div>

        {fallback && data?.configured && (
          <div className="mt-2 p-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Lane OTP não configurado — usando bulk como fallback.
          </div>
        )}

        {data?.error && (
          <div className="mt-2 p-1.5 bg-red-50 border border-red-200 rounded text-[10px] text-red-700">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            {data.error.slice(0, 160)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminZapiHealth = () => {
  const { token } = useAuth();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testLane, setTestLane] = useState("bulk");
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLane, setHistoryLane] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/zapi-health`, {
        headers,
        timeout: 20000,
      });
      setHealth(res.data || null);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Erro ao verificar Z-API");
      }
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadHistory = useCallback(
    async (laneFilter = null) => {
      setLoadingHistory(true);
      try {
        const params = new URLSearchParams({ limit: "50" });
        if (laneFilter) params.set("lane", laneFilter);
        const res = await axios.get(
          `${API}/admin/zapi-health/history?${params.toString()}`,
          { headers, timeout: 15000 },
        );
        setHistory(res.data?.history || []);
      } catch (e) {
        if (e?.response?.status !== 401) {
          toast.error(e?.response?.data?.detail || "Erro ao carregar histórico");
        }
      } finally {
        setLoadingHistory(false);
      }
    },
    [headers],
  );

  useEffect(() => {
    load();
  }, [load]);

  const openTest = (lane) => {
    setTestLane(lane);
    setTestOpen(true);
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error("Informe um telefone");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/zapi-health/test-message`,
        {
          phone: testPhone.trim(),
          message: testMessage.trim() || undefined,
          lane: testLane,
        },
        { headers, timeout: 30000 },
      );
      const d = res.data || {};
      if (d.sent) {
        const laneTxt = d.lane === "otp" ? "Lane OTP" : "Lane Bulk";
        toast.success(`Mensagem enviada via ${laneTxt}!`);
        setTestOpen(false);
        load();
      } else {
        toast.error(d.error || "Falha ao enviar — confira o número e a conexão");
      }
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Erro ao enviar teste");
      }
    } finally {
      setSending(false);
    }
  };

  const openHistory = (lane = null) => {
    setHistoryLane(lane);
    setHistoryOpen(true);
    loadHistory(lane);
  };

  if (!health && loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-4 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Verificando Z-API…
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">
              Saúde do WhatsApp (Z-API)
            </h3>
            <span className="text-[10px] text-slate-400">
              · throttle {health?.bulk?.throttle_seconds || 5}s/msg
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openHistory(null)}
            data-testid="zapi-history-btn"
          >
            <History className="w-3.5 h-3.5 mr-1" />
            Histórico
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LaneCard
            lane="otp"
            data={health?.otp}
            loading={loading}
            onTest={openTest}
            onRefresh={load}
          />
          <LaneCard
            lane="bulk"
            data={health?.bulk}
            loading={loading}
            onTest={openTest}
            onRefresh={load}
          />
        </div>
      </div>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={(o) => !o && setHistoryOpen(false)}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="zapi-history-dialog"
        >
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <DialogTitle className="flex items-center gap-2">
                <History className="w-4 h-4 text-sky-600" />
                Histórico de envios Z-API
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant={historyLane === null ? "default" : "outline"}
                  onClick={() => { setHistoryLane(null); loadHistory(null); }}
                  className="h-7 px-2 text-xs"
                  data-testid="zapi-history-filter-all"
                >
                  Todas
                </Button>
                <Button
                  size="sm"
                  variant={historyLane === "otp" ? "default" : "outline"}
                  onClick={() => { setHistoryLane("otp"); loadHistory("otp"); }}
                  className="h-7 px-2 text-xs"
                  data-testid="zapi-history-filter-otp"
                >
                  <Shield className="w-3 h-3 mr-1" />OTP
                </Button>
                <Button
                  size="sm"
                  variant={historyLane === "bulk" ? "default" : "outline"}
                  onClick={() => { setHistoryLane("bulk"); loadHistory("bulk"); }}
                  className="h-7 px-2 text-xs"
                  data-testid="zapi-history-filter-bulk"
                >
                  <Zap className="w-3 h-3 mr-1" />Bulk
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadHistory(historyLane)}
                  disabled={loadingHistory}
                  className="h-7 px-2"
                  data-testid="zapi-history-refresh-btn"
                >
                  {loadingHistory ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
            <DialogDescription>
              Últimos {history.length} envios{historyLane ? ` (lane: ${historyLane})` : ""} — memória do servidor (zera em restart).
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto border border-slate-200 rounded-md" data-testid="zapi-history-list">
            {history.length === 0 && !loadingHistory && (
              <div className="p-8 text-center text-sm text-slate-500">
                Nenhum envio registrado.
              </div>
            )}
            {history.length === 0 && loadingHistory && (
              <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Carregando…
              </div>
            )}
            {history.length > 0 && (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-[10px] uppercase text-slate-500 tracking-wide">
                    <th className="p-2 font-medium">Quando</th>
                    <th className="p-2 font-medium">Lane</th>
                    <th className="p-2 font-medium">Tipo</th>
                    <th className="p-2 font-medium">Destinatário</th>
                    <th className="p-2 font-medium">Status</th>
                    <th className="p-2 font-medium">Detalhes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((h, i) => {
                    const KindIcon =
                      h.kind === "image" ? ImageIcon :
                      h.kind === "video" ? Video :
                      h.kind === "document" ? FileText :
                      MessageSquare;
                    const LaneTag = h.lane === "otp" ? Shield : Zap;
                    let dt = "";
                    try {
                      dt = new Date(h.ts).toLocaleString("pt-BR", {
                        day: "2-digit", month: "2-digit",
                        hour: "2-digit", minute: "2-digit", second: "2-digit",
                      });
                    } catch { dt = h.ts; }
                    return (
                      <tr
                        key={i}
                        className={h.success ? "" : "bg-red-50/40"}
                        data-testid={`zapi-history-row-${i}`}
                      >
                        <td className="p-2 font-mono text-[10px] text-slate-600 whitespace-nowrap">
                          {dt}
                        </td>
                        <td className="p-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${h.lane === "otp" ? "text-violet-700" : "text-sky-700"}`}>
                            <LaneTag className="w-3 h-3" />
                            {h.lane || "bulk"}
                          </span>
                        </td>
                        <td className="p-2">
                          <span className="inline-flex items-center gap-1 text-slate-700">
                            <KindIcon className="w-3 h-3" />
                            {h.kind}
                          </span>
                        </td>
                        <td className="p-2 font-mono text-slate-700">
                          {h.phone || "—"}
                        </td>
                        <td className="p-2">
                          {h.success ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3" /> OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-700">
                              <XCircle className="w-3 h-3" /> Falha
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-slate-600 max-w-xs">
                          <div className="truncate" title={h.msg_preview || h.error}>
                            {h.success ? (
                              h.msg_preview || h.zaap_id || "—"
                            ) : (
                              <span className="text-red-700">{h.error || "—"}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {history.length > 0 && (
            <div className="flex-shrink-0 text-[11px] text-slate-500 mt-2 flex items-center gap-3">
              <span>
                <CheckCircle2 className="w-3 h-3 inline text-emerald-600 mr-0.5" />
                {history.filter((h) => h.success).length} OK
              </span>
              <span>
                <XCircle className="w-3 h-3 inline text-red-600 mr-0.5" />
                {history.filter((h) => !h.success).length} falha(s)
              </span>
              <span className="ml-auto">
                Sucesso: <strong>
                  {history.length > 0
                    ? Math.round(
                        (history.filter((h) => h.success).length / history.length) * 100,
                      )
                    : 0}
                  %
                </strong>
              </span>
            </div>
          )}

          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test message dialog */}
      <Dialog open={testOpen} onOpenChange={(o) => !o && !sending && setTestOpen(false)}>
        <DialogContent data-testid="zapi-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-700">
              <Send className="w-4 h-4" />
              Testar envio via {testLane === "otp" ? "Lane OTP" : "Lane Bulk"}
            </DialogTitle>
            <DialogDescription>
              Envia uma mensagem de teste via{" "}
              {testLane === "otp"
                ? "a instância dedicada de OTP"
                : "a instância de broadcasts"}{" "}
              pro número informado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Telefone (DDI 55 + DDD + número)
              </label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="5571999998888"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="zapi-test-phone"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Mensagem (opcional)
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                placeholder="Em branco usa a mensagem padrão de teste"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="zapi-test-message"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestOpen(false)}
              disabled={sending}
            >
              Cancelar
            </Button>
            <Button
              onClick={sendTest}
              disabled={sending}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="zapi-test-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Enviando…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1.5" />
                  Enviar teste
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminZapiHealth;
